"use client";

import { Dialog, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

// The overlay, sliding panel and focus handling every drawer shares, with no
// content chrome. `Drawer` below builds its form, header and footer on it; a
// caller with its own chrome (the Integrations drawer) uses it directly.
// `maxWidthClassName` replaces the `size` width when a drawer needs one that
// is not a Tailwind step.
export function DrawerShell({
  open,
  onClose,
  children,
  position = "right", // "left", "right", "top", "bottom"
  size = "md", // "sm", "md", "lg", "xl", "full"
  maxWidthClassName = null,
  className = "",
  panelClassName = "",
}) {
  const getPositionClasses = () => {
    switch (position) {
      case "left":
        return "left-0";
      case "right":
        return "right-0";
      case "top":
        return "top-0 left-0 right-0";
      case "bottom":
        return "bottom-0 left-0 right-0";
      default:
        return "right-0";
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "max-w-sm";
      case "md":
        return "max-w-md";
      case "lg":
        return "max-w-lg";
      case "xl":
        return "max-w-xl";
      case "full":
        return "max-w-full";
      default:
        return "max-w-md";
    }
  };

  const getTransformClasses = () => {
    switch (position) {
      case "left":
        return "data-closed:-translate-x-full";
      case "right":
        return "data-closed:translate-x-full";
      case "top":
        return "data-closed:-translate-y-full";
      case "bottom":
        return "data-closed:translate-y-full";
      default:
        return "data-closed:translate-x-full";
    }
  };

  const getFlexDirection = () => {
    switch (position) {
      case "left":
        return "flex-row";
      case "right":
        return "flex-row";
      case "top":
        return "flex-col";
      case "bottom":
        return "flex-col";
      default:
        return "flex-row";
    }
  };

  const getPaddingClasses = () => {
    switch (position) {
      case "left":
        return "pr-10 sm:pr-16";
      case "right":
        return "pl-10 sm:pl-16";
      case "top":
        return "pb-10 sm:pb-16";
      case "bottom":
        return "pt-10 sm:pt-16";
      default:
        return "pl-10 sm:pl-16";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={`relative z-92147483003 ${className}`}
    >
      {/* Overlay - exactly like your example */}
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute bg-gray-900/40 inset-0 overflow-hidden">
          <div
            className={`pointer-events-none fixed inset-y-0 ${getPositionClasses()} flex ${getFlexDirection()} ${getPaddingClasses()}`}
          >
            <DialogPanel
              transition
              className={`pointer-events-auto w-screen ${maxWidthClassName || getSizeClasses()} transform transition duration-500 ease-in-out ${getTransformClasses()} sm:duration-700 ${panelClassName}`}
            >
              {children}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default function Drawer({
  open,
  setOpen,
  children,
  position = "right", // "left", "right", "top", "bottom"
  size = "md", // "sm", "md", "lg", "xl", "full"
  showCloseButton = true,
  closeOnOverlayClick = true,
  className = "",
  panelClassName = "",
  header = null, // Optional header content
  headerClassName = "bg-gray-900 text-white",
  onSubmit = null,
  onCancel = null,
  submitText = "Save",
  cancelText = "Cancel",
  isLoading = false,
  submitDisabled = false,
  showFooter = true,
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setOpen(false);
    }
  };

  return (
    <DrawerShell
      open={open}
      onClose={closeOnOverlayClick ? setOpen : undefined}
      position={position}
      size={size}
      className={className}
      panelClassName={panelClassName}
    >
      <form
        onSubmit={handleSubmit}
        className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      >
        {/* Header */}
        {header && (
          <div className={`px-4 py-6 sm:px-6 ${headerClassName}`}>
            <div className="flex items-center justify-between">
              {header}
              {showCloseButton && (
                <div className="ml-3 flex h-7 items-center">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="relative rounded-md bg-gray-900 text-white hover:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-hidden"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="h-0 flex-1 overflow-y-auto">{children}</div>

        {/* Footer with form buttons */}
        {showFooter && <div className="flex shrink-0 justify-end px-4 py-4">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
          >
            {cancelText}
          </button>
          <button
            type="submit"
            disabled={submitDisabled || isLoading}
            className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Saving..." : submitText}
          </button>
        </div>}
      </form>
    </DrawerShell>
  );
}
